import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import { apiService } from "../api/services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { useIsCsr } from "../hooks/useIsCsr";
import { useIsProductManager } from "../hooks/useIsProductManager";
import type { SupportCase, SupportCaseComment, SupportCaseEvent } from "../types";
import {
  SupportCaseCommentType,
  SupportCaseStatus,
} from "../types";
import {
  SUPPORT_CASE_STATUS_LABELS,
  supportCaseStatusColor,
} from "../utils/supportCaseLabels";

type TimelineItem =
  | { kind: "event"; at: string; data: SupportCaseEvent }
  | { kind: "comment"; at: string; data: SupportCaseComment };

const AGENT_STATUSES = [
  SupportCaseStatus.IN_PROGRESS,
  SupportCaseStatus.AWAITING_RESPONSE,
  SupportCaseStatus.RESOLVED,
];

const SupportCaseDetailPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCsr = useIsCsr();
  const isPm = useIsProductManager();
  const isAgent = isCsr || isPm;

  const [supportCase, setSupportCase] = useState<SupportCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [targetStatus, setTargetStatus] = useState<SupportCaseStatus>(
    SupportCaseStatus.IN_PROGRESS,
  );

  const returnQuery = searchParams.toString();
  const listPath = returnQuery ? `/support-cases?${returnQuery}` : "/support-cases";
  const userEmail = user?.email?.toLowerCase() ?? "";
  const isCreator =
    supportCase && userEmail === supportCase.createdBy.toLowerCase();

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getSupportCase(caseId);
      setSupportCase(data);
      setTargetStatus(
        data.caseStatus === SupportCaseStatus.AWAITING_RESPONSE
          ? SupportCaseStatus.IN_PROGRESS
          : SupportCaseStatus.AWAITING_RESPONSE,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load support case");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const timeline = useMemo((): TimelineItem[] => {
    if (!supportCase) return [];
    const items: TimelineItem[] = [];
    (supportCase.events ?? []).forEach((e) =>
      items.push({ kind: "event", at: e.createdOn, data: e }),
    );
    (supportCase.comments ?? []).forEach((c) =>
      items.push({ kind: "comment", at: c.createdOn, data: c }),
    );
    return items.sort(
      (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
    );
  }, [supportCase]);

  const canClaim =
    isAgent &&
    supportCase &&
    (supportCase.caseStatus === SupportCaseStatus.OPEN ||
      (supportCase.caseStatus === SupportCaseStatus.ASSIGNED &&
        supportCase.assigneeEmail?.toLowerCase() === userEmail));

  const canTransition =
    isAgent &&
    supportCase &&
    [
      SupportCaseStatus.IN_PROGRESS,
      SupportCaseStatus.AWAITING_RESPONSE,
      SupportCaseStatus.ASSIGNED,
    ].includes(supportCase.caseStatus);

  const runAction = async (fn: () => Promise<void>) => {
    setActionLoading(true);
    try {
      await fn();
      await load();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle={supportCase?.name ?? "Support Case"}
        pageDescription="Case details, timeline, and actions."
      />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(listPath)}
          sx={{ mb: 3, textTransform: "none" }}
        >
          Back to support cases
        </Button>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && supportCase && (
          <>
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Stack spacing={2}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                  <Typography variant="h5" fontWeight={600}>
                    {supportCase.name}
                  </Typography>
                  <Chip
                    label={SUPPORT_CASE_STATUS_LABELS[supportCase.caseStatus]}
                    color={supportCaseStatusColor(supportCase.caseStatus)}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                  {supportCase.description || "—"}
                </Typography>
                <Divider />
                <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { sm: "1fr 1fr" } }}>
                  <Typography variant="body2">
                    <strong>Created by:</strong> {supportCase.createdBy}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Assignee:</strong> {supportCase.assigneeEmail ?? "—"}
                    {supportCase.assigneeType ? ` (${supportCase.assigneeType})` : ""}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Product:</strong> {supportCase.product?.name ?? "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created:</strong>{" "}
                    {new Date(supportCase.createdOn).toLocaleString()}
                  </Typography>
                </Box>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {canClaim && (
                    <Button
                      variant="contained"
                      disabled={actionLoading}
                      onClick={() =>
                        runAction(async () => {
                          await apiService.claimSupportCase(supportCase.uuid);
                        })
                      }
                    >
                      Pick case
                    </Button>
                  )}
                  {isCsr && (
                    <Button
                      variant="outlined"
                      disabled={actionLoading}
                      onClick={() => setAssignOpen(true)}
                    >
                      Assign to CSR
                    </Button>
                  )}
                  {canTransition && (
                    <>
                      <TextField
                        select
                        size="small"
                        label="Change status"
                        value={targetStatus}
                        onChange={(e) =>
                          setTargetStatus(e.target.value as SupportCaseStatus)
                        }
                        sx={{ minWidth: 200 }}
                      >
                        {AGENT_STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>
                            {SUPPORT_CASE_STATUS_LABELS[s]}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        variant="outlined"
                        disabled={actionLoading}
                        onClick={() =>
                          runAction(async () => {
                            await apiService.transitionSupportCaseStatus(
                              supportCase.uuid,
                              targetStatus,
                            );
                          })
                        }
                      >
                        Update status
                      </Button>
                    </>
                  )}
                  {isCreator && supportCase.caseStatus === SupportCaseStatus.RESOLVED && (
                    <Button
                      color="success"
                      variant="contained"
                      disabled={actionLoading}
                      onClick={() =>
                        runAction(async () => {
                          await apiService.acceptSupportCaseResolution(supportCase.uuid);
                        })
                      }
                    >
                      Accept resolution
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Timeline
              </Typography>
              <Stack spacing={2}>
                {timeline.length === 0 && (
                  <Typography color="text.secondary">No activity yet.</Typography>
                )}
                {timeline.map((item) =>
                  item.kind === "comment" ? (
                    <Box
                      key={item.data.uuid}
                      sx={{
                        pl: 2,
                        borderLeft: 3,
                        borderColor:
                          item.data.commentType === SupportCaseCommentType.SYSTEM
                            ? "grey.400"
                            : "primary.main",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {item.data.commentType} · {item.data.authorEmail} ·{" "}
                        {new Date(item.at).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {item.data.body}
                      </Typography>
                    </Box>
                  ) : (
                    <Box key={item.data.uuid} sx={{ pl: 2, borderLeft: 3, borderColor: "grey.300" }}>
                      <Typography variant="caption" color="text.secondary">
                        {item.data.eventType} · {item.data.actorEmail} ·{" "}
                        {new Date(item.at).toLocaleString()}
                      </Typography>
                      <Typography variant="body2">{item.data.message}</Typography>
                    </Box>
                  ),
                )}
              </Stack>
            </Paper>

            {(isCreator || isAgent) && supportCase.caseStatus !== SupportCaseStatus.CLOSED && (
              <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add comment
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    isCreator
                      ? "Reply to the support team..."
                      : "Instructions or updates for the case creator..."
                  }
                />
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  disabled={actionLoading || !comment.trim()}
                  onClick={() =>
                    runAction(async () => {
                      await apiService.addSupportCaseComment(
                        supportCase.uuid,
                        comment.trim(),
                        isCreator
                          ? SupportCaseCommentType.RESPONSE
                          : SupportCaseCommentType.INSTRUCTION,
                      );
                      setComment("");
                    })
                  }
                >
                  Post comment
                </Button>
              </Paper>
            )}
          </>
        )}
      </Container>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Assign to CSR</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="CSR email"
            value={assigneeEmail}
            onChange={(e) => setAssigneeEmail(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!assigneeEmail.trim() || actionLoading}
            onClick={() =>
              runAction(async () => {
                if (!supportCase) return;
                await apiService.assignSupportCase(
                  supportCase.uuid,
                  assigneeEmail.trim(),
                );
                setAssignOpen(false);
                setAssigneeEmail("");
              })
            }
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportCaseDetailPage;
